import React from 'react';
import errorLogger from '../utils/ErrorLogger';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null, 
      errorInfo: null,
      errorId: null
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Log the error with full context
    const errorId = errorLogger.logError(error, 'react-error-boundary', {
      componentStack: errorInfo.componentStack,
      errorBoundary: this.props.name || 'Unknown',
      props: this.props.logProps ? this.props : undefined
    });

    this.setState({
      error,
      errorInfo,
      errorId
    });

    // Track this as a critical user-facing error
    errorLogger.logUserAction('error-boundary-triggered', {
      errorMessage: error.message,
      componentStack: errorInfo.componentStack,
      errorId
    });
  }

  handleRetry = () => {
    errorLogger.logUserAction('error-boundary-retry', {
      errorId: this.state.errorId,
      boundaryName: this.props.name
    });
    
    this.setState({ 
      hasError: false, 
      error: null, 
      errorInfo: null,
      errorId: null
    });
  };

  handleReload = () => {
    errorLogger.logUserAction('error-boundary-reload', {
      errorId: this.state.errorId,
      boundaryName: this.props.name
    });
    
    window.location.reload();
  };

  handleGoHome = () => {
    errorLogger.logUserAction('error-boundary-go-home', {
      errorId: this.state.errorId,
      boundaryName: this.props.name
    });
    
    // Clear error state and navigate to home
    this.setState({ 
      hasError: false, 
      error: null, 
      errorInfo: null,
      errorId: null
    });
    
    // Use window.location to ensure clean navigation
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      // Enhanced fallback UI for app-level error boundary
      return (
        <div className="min-vh-100 d-flex align-items-center justify-content-center bg-light">
          <div className="container">
            <div className="row justify-content-center">
              <div className="col-md-8 col-lg-6">
                <div className="card shadow">
                  <div className="card-body p-4">
                    <div className="text-center mb-4">
                      <svg 
                        className="text-danger mb-3" 
                        width="64" 
                        height="64" 
                        fill="currentColor" 
                        viewBox="0 0 16 16"
                      >
                        <path d="M8.982 1.566a1.13 1.13 0 0 0-1.96 0L.165 13.233c-.457.778.091 1.767.98 1.767h13.713c.889 0 1.438-.99.98-1.767L8.982 1.566zM8 5c.535 0 .954.462.9.995l-.35 3.507a.552.552 0 0 1-1.1 0L7.1 5.995A.905.905 0 0 1 8 5zm.002 6a1 1 0 1 1 0 2 1 1 0 0 1 0-2z"/>
                      </svg>
                      <h2 className="h4 mb-2">Oops! Something went wrong</h2>
                      <p className="text-muted mb-0">
                        We encountered an unexpected error. Don't worry - this has been logged 
                        and our team will investigate.
                      </p>
                    </div>

                    {this.state.errorId && (
                      <div className="alert alert-light border mb-4">
                        <small className="text-muted d-block">
                          <strong>Error Reference:</strong> <code>{this.state.errorId}</code>
                        </small>
                        <small className="text-muted">
                          Please include this reference if you contact support.
                        </small>
                      </div>
                    )}

                    <div className="d-grid gap-2">
                      <button 
                        className="btn btn-primary" 
                        onClick={this.handleRetry}
                      >
                        <svg className="me-2" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                          <path fillRule="evenodd" d="M8 3a5 5 0 1 0 4.546 2.914.5.5 0 0 1 .908-.417A6 6 0 1 1 8 2v1z"/>
                          <path d="M8 4.466V.534a.25.25 0 0 1 .41-.192l2.36 1.966c.12.1.12.284 0 .384L8.41 4.658A.25.25 0 0 1 8 4.466z"/>
                        </svg>
                        Try Again
                      </button>
                      
                      <div className="row g-2">
                        <div className="col">
                          <button 
                            className="btn btn-outline-secondary w-100" 
                            onClick={this.handleGoHome}
                          >
                            <svg className="me-2" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                              <path d="m8 3.293 6 6V13.5a1.5 1.5 0 0 1-1.5 1.5h-9A1.5 1.5 0 0 1 2 13.5V9.293l6-6zm5-.793V6l-2-2V2.5a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5z"/>
                              <path d="M7.293 1.5a1 1 0 0 1 1.414 0l6.647 6.646a.5.5 0 0 1-.708.708L8 2.207 1.354 8.854a.5.5 0 1 1-.708-.708L7.293 1.5z"/>
                            </svg>
                            Go Home
                          </button>
                        </div>
                        <div className="col">
                          <button 
                            className="btn btn-outline-secondary w-100" 
                            onClick={this.handleReload}
                          >
                            <svg className="me-2" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                              <path fillRule="evenodd" d="M8 3a5 5 0 1 0 4.546 2.914.5.5 0 0 1 .908-.417A6 6 0 1 1 8 2v1z"/>
                              <path d="M8 4.466V.534a.25.25 0 0 1 .41-.192l2.36 1.966c.12.1.12.284 0 .384L8.41 4.658A.25.25 0 0 1 8 4.466z"/>
                            </svg>
                            Reload Page
                          </button>
                        </div>
                      </div>
                    </div>

                    {process.env.NODE_ENV === 'development' && this.state.error && (
                      <details className="mt-4">
                        <summary className="btn btn-link p-0 text-decoration-none">
                          <small>🔧 Show Technical Details (Development Only)</small>
                        </summary>
                        <div className="mt-3">
                          <div className="mb-3">
                            <h6 className="small text-muted mb-1">Error Message:</h6>
                            <pre className="bg-light p-2 small border rounded">
                              {this.state.error.message}
                            </pre>
                          </div>
                          
                          <div className="mb-3">
                            <h6 className="small text-muted mb-1">Stack Trace:</h6>
                            <pre className="bg-light p-2 border rounded" style={{ fontSize: '0.7rem', maxHeight: '200px', overflow: 'auto' }}>
                              {this.state.error.stack}
                            </pre>
                          </div>
                          
                          {this.state.errorInfo && (
                            <div className="mb-3">
                              <h6 className="small text-muted mb-1">Component Stack:</h6>
                              <pre className="bg-light p-2 border rounded" style={{ fontSize: '0.7rem', maxHeight: '150px', overflow: 'auto' }}>
                                {this.state.errorInfo.componentStack}
                              </pre>
                            </div>
                          )}

                          {this.props.name && (
                            <div>
                              <h6 className="small text-muted mb-1">Error Boundary:</h6>
                              <code className="bg-light p-1 border rounded">{this.props.name}</code>
                            </div>
                          )}
                        </div>
                      </details>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;